import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, MapPin, FolderLock, CalendarDays, Newspaper, Users, LogOut, Users2, Link as LinkIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const links = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/agents", label: "Agent Locations", icon: MapPin },
  { to: "/portal/employees", label: "Employee Directory", icon: Users2 },
  { to: "/portal/documents", label: "Document Hub", icon: FolderLock },
  { to: "/portal/holidays", label: "Holidays", icon: CalendarDays },
  { to: "/portal/news", label: "News", icon: Newspaper },
  { to: "/portal/important-links", label: "Important Links", icon: LinkIcon },
];

export default function PortalLayout() {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-white flex">
      <aside className="hidden md:flex w-64 flex-col bg-[#F8F9FA] border-r border-gray-200 sticky top-0 h-screen" data-testid="portal-sidebar">
        <div className="p-6 border-b border-gray-200">
          <img src="/assets/TRANSLOG SECONDARY LOGO.png" alt="Translog Express" className="h-9 w-auto object-contain" />
          <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-gray-500">Employee Portal</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white text-[#DC2A1C] border-l-2 border-[#DC2A1C] shadow-sm" : "text-gray-700 hover:bg-white/70"}`
              }
              data-testid={`sidebar-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/portal/users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white text-[#DC2A1C] border-l-2 border-[#DC2A1C] shadow-sm" : "text-gray-700 hover:bg-white/70"}`
              }
              data-testid="sidebar-users"
            >
              <Users className="w-4 h-4" /> User Management
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-gray-200">
          <div className="mb-3">
            <div className="text-xs font-semibold truncate" data-testid="user-email">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#DC2A1C] font-semibold mt-0.5">{user?.role}</div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/assets/logo.png" alt="Translog" className="h-8 w-auto object-contain" />
          <div className="font-black text-sm text-[#1A1A1A]">Portal</div>
        </div>
        <button onClick={handleLogout} className="text-xs text-gray-600"><LogOut className="w-4 h-4" /></button>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="md:hidden border-b border-gray-100 bg-white px-4 py-2 overflow-x-auto">
          <div className="flex gap-2 whitespace-nowrap">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}
                className={({ isActive }) => `px-3 py-1.5 rounded-full text-xs font-medium ${isActive ? "bg-[#DC2A1C] text-white" : "bg-gray-100 text-gray-700"}`}
              >{l.label}</NavLink>
            ))}
            {isAdmin && <NavLink to="/portal/users" className={({ isActive }) => `px-3 py-1.5 rounded-full text-xs font-medium ${isActive ? "bg-[#DC2A1C] text-white" : "bg-gray-100 text-gray-700"}`}>Users</NavLink>}
          </div>
        </div>
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
